import {
  Badge,
  Box,
  BoxProps,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography
} from "@mui/material";
import React from "react";
import {Close} from "@mui/icons-material";
import {ObjectListItem} from "../lists/ObjectListItem";
import Pjdb1Button from "../../../../components/inputs/Pjdb1Button";
import {ObjectDocument} from "../../../../types/pj1dbApiObject";
import {FetchMessage, Status, STATUSES} from "../../../../hooks/useFetchAllQuery";
import SearchForm, {SearchCond} from "./SearchForm";
import SortOrder from "./SortOrder";
import {OBJECT_LIST_SORT_ORDERS, ObjectListSortOrderValue} from "../../../../constants/constants";
import CloseIconButton from "../buttons/CloseIconButton";
import Pjdb1ToolTip from "../../../../components/inputs/Pjdb1ToolTip";

export interface ObjectSelectionFieldProps extends Omit<ObjectListProps, 'onClick'> {
  onChange: (objects: ObjectDocument[]) => void;
}

export function ObjectSelectionField({onChange, selectedObjects, ...props}: ObjectSelectionFieldProps) {
  const [open, setOpen] = React.useState(false);

  return (
    <>
      <Stack gap={3}>
        <Box display='flex' flexWrap='nowrap' gap={2}>
          <Pjdb1Button
            label='環境オブジェクトを選択する'
            variant='outlined'
            color='inherit'
            onClick={() => setOpen(true)}
            sx={{width: 'fit-content'}}
          />
          <Pjdb1ToolTip
            toolTip='環境オブジェクトを選択するを押下し、ポップアップ画面で空ベースワールドに配置する環境オブジェクトを検索して選択してください。'/>
        </Box>
        <Box display='flex' flexWrap='wrap' width='100%' gap={3}>
          {selectedObjects.map((obj) => (
            <Box key={obj.attr['object-id']}>
              <Badge
                badgeContent={
                  <Close
                    fontSize='medium'
                    onClick={() => onChange(selectedObjects.filter((o) => o.attr["object-id"] !== obj.attr["object-id"]))}
                    sx={{color: 'white', bgcolor: 'gray', borderRadius: 4}
                    }/>
                }>
                <ObjectListItem item={obj}/>
              </Badge>
            </Box>
          ))}
        </Box>
      </Stack>
      <Dialog disableEscapeKeyDown open={open} onClose={() => setOpen(false)} fullWidth maxWidth='xl'>
        <DialogTitle>
          <Box display='flex' justifyContent='space-between' alignItems='center'>
            環境オブジェクトを選択
            <CloseIconButton onClose={() => setOpen(false)}/>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box bgcolor='#F6F7F8' py={4} px={2}>
            <ObjectList
              /* eslint-disable-next-line react/jsx-props-no-spreading */
              {...props}
              selectedObjects={selectedObjects}
              onClick={(obj) => {
                if (selectedObjects.some((o) => o.attr['object-id'] === obj.attr['object-id'])) {
                  onChange(selectedObjects.filter((o) => o.attr['object-id'] !== obj.attr['object-id']));
                  return;
                }
                onChange([...selectedObjects, obj]);
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Pjdb1Button label="閉じる" color='inherit' variant='contained' onClick={() => setOpen(false)}/>
        </DialogActions>
      </Dialog>
    </>
  )
}

interface ObjectListProps {
  objects: ObjectDocument[];
  selectedObjects: ObjectDocument[];
  onClick: (obj: ObjectDocument) => void;
  onChangeSearchCond: (searchCond: SearchCond<ObjectListSortOrderValue>) => void;
  fetchObjects: () => Promise<void>;
  searchCond: SearchCond<ObjectListSortOrderValue>;
  searchStatus: Status;
}

function ObjectList(props: ObjectListProps) {
  const selectedStyle: BoxProps['sx'] = {
    outlineColor: (theme) => theme.palette.primary.light,
    outlineStyle: "solid",
    outlineWidth: 6,
    borderRadius: 1,
  };

  return (
    <Stack gap={4}>
      <SearchForm
        label="パッケージ名、オブジェクト名などを入力"
        isLoading={props.searchStatus === STATUSES.FETCHING}
        searchCond={props.searchCond}
        onChange={props.onChangeSearchCond}
        onSubmit={(e) => {
          e.preventDefault();
          props.fetchObjects().catch(console.error);
        }}
      />

      <Box display='flex' flexWrap='nowrap' justifyContent='space-between'>
        <Typography variant="subtitle1" sx={{fontWeight: "bold"}}>
          検索結果一覧
        </Typography>
        <Box display='flex' gap={2}>
          <Box>
            {props.objects.length > 0 ? `${props.objects.length.toLocaleString()}件` : ""}
          </Box>
          <SortOrder
            value={props.searchCond.sortOrder}
            onChange={(event) => {
              props.onChangeSearchCond({
                ...props.searchCond,
                sortOrder: event.target.value as ObjectListSortOrderValue
              });
            }}
            items={Object.values(OBJECT_LIST_SORT_ORDERS)}/>
        </Box>
      </Box>

      {props.objects.length > 0 && (
        <Box display='flex' flexWrap='wrap' gap={2}>
          {props.objects.map((obj) => (
            <ObjectListItem
              key={obj.attr["object-id"]}
              item={obj}
              onClick={() => props.onClick(obj)}
              sx={{
                outlineColor: 'inherit',
                outlineStyle: "solid",
                outlineWidth: 1,
                borderRadius: 1,
                ...(
                  props.selectedObjects.some((o) => o.attr["object-id"] === obj.attr["object-id"])
                    ? selectedStyle
                    : {}
                )
              }}
            />
          ))}
        </Box>
      )}

      <FetchMessage data={props.objects} status={props.searchStatus}/>
    </Stack>
  )
}
